/* eslint-disable @next/next/no-img-element */
import {
  SeasonFragment,
  TeamListItemFragment,
  TeamRosterFragment,
  TeamRosterPlayerFragment,
} from '@amf/shared/types/graphql';
import { useTranslate } from '@tolgee/react';
import Link from 'next/link';
import ButtonIcon from '@amf/shared/components/button/ButtonIcon';
import { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';
import { getCacheKey } from '@amf/shared/data/TeamRoster';
import TeamRosterFilter, {
  TeamRosterFilterData,
} from '@amf/shared/components/team/TeamRosterFilter';
import { getAge } from '@amf/shared/utils/date';
import { findTeamDefaults } from '@amf/shared/data/Team';

interface TeamRosterPlayerProps {
  player: TeamRosterPlayerFragment;
  subtitle: string;
  prefix: string;
  disableAssists?: boolean;
  disablePoints?: boolean;
  facr?: boolean;
}

function TeamRosterPlayer({
  player,
  subtitle,
  prefix,
  disableAssists,
  disablePoints,
  facr,
}: TeamRosterPlayerProps) {
  const { t } = useTranslate();
  return (
    <tr>
      <td className='TeamRoster__cell TeamRoster__cell--avatar'>
        <Link href={`${prefix}/${player.player.id}`} passHref>
          <a>
            {player.player.photoUrl && (
              <img
                className='TeamRoster__avatar'
                src={player.player.photoUrl}
                alt={player.player.fullName}
              />
            )}
            {!player.player.photoUrl && (
              <div className='TeamRoster__avatar TeamRoster__avatar--placeholder' />
            )}
          </a>
        </Link>
      </td>
      <td className='TeamRoster__cell'>
        <Link href={`${prefix}/${player.player.id}`} passHref>
          <a>
            <strong className='TeamRoster__cellTitle'>{player.player.fullName}</strong>
            <span className='TeamRoster__cellSubtitle'>
              {subtitle} {player.player.facr && facr ? <> &bull; FAČR</> : null}
            </span>
          </a>
        </Link>
      </td>
      <td className='TeamRoster__cell'>
        {t('team.roster.age', { age: getAge(player.player.birthDate) })}
      </td>
      <td className='TeamRoster__cell TeamRoster__cell--center'>{player.stats.matches}</td>
      <td className='TeamRoster__cell TeamRoster__cell--center'>{player.stats.goals}</td>
      {!disableAssists && (
        <td className='TeamRoster__cell TeamRoster__cell--center'>{player.stats.assists}</td>
      )}
      {!disablePoints && (
        <td className='TeamRoster__cell TeamRoster__cell--center'>{player.stats.points}</td>
      )}
      <td className='TeamRoster__cell TeamRoster__cell--center'>{player.stats.yellowCards}</td>
      <td className='TeamRoster__cell TeamRoster__cell--center'>{player.stats.redCards}</td>
      <td className='TeamRoster__cell TeamRoster__cell--right'>
        <Link href={`${prefix}/${player.player.id}`}>
          <a>
            <ButtonIcon variant='dark'>
              <i className='bi bi-arrow-right-circle-fill' />
            </ButtonIcon>
          </a>
        </Link>
      </td>
    </tr>
  );
}

interface TeamRosterProps {
  seasons: SeasonFragment[];
  team: TeamListItemFragment;
  prefix: string;
  title?: string;
  disableAssists?: boolean;
  disablePoints?: boolean;
  facr?: boolean;
}

function TeamRoster({
  seasons,
  prefix,
  title,
  team,
  disableAssists,
  disablePoints,
  facr,
}: TeamRosterProps) {
  const { t } = useTranslate();
  const [filter, setFilter] = useState<TeamRosterFilterData>({
    season: seasons[0].id,
    league: seasons[0].leagues[0].id,
  });
  const { data: roster } = useSWR<TeamRosterFragment | null>(
    getCacheKey({ ...filter, team: team.id }),
  );

  useEffect(() => {
    const { league, season } = findTeamDefaults(team, seasons);
    if (league && season) {
      setFilter({ season: season.id, league: league.id });
    }
  }, [seasons, team]);

  const update = useCallback(async (data: TeamRosterFilterData) => {
    setFilter(data);
  }, []);

  return (
    <div className='TeamRoster'>
      <div className='TeamRoster__head'>
        {title && <h2>{title}</h2>}
        <TeamRosterFilter team={team} seasons={seasons} onUpdate={update} />
      </div>
      <div className='TeamRoster__content'>
        <table className='TeamRoster__table'>
          <thead>
            <tr>
              <th colSpan={2} className='TeamRoster__header'>
                {t('team.roster.player')}
              </th>
              <th className='TeamRoster__header'>{t('team.roster.birthDate')}</th>
              <th className='TeamRoster__header TeamRoster__header--center'>
                {t('player.stats.matches')}
              </th>
              <th className='TeamRoster__header TeamRoster__header--center'>
                {t('player.stats.goals')}
              </th>
              {!disableAssists && (
                <th className='TeamRoster__header TeamRoster__header--center'>
                  {t('player.stats.assists')}
                </th>
              )}
              {!disablePoints && (
                <th className='TeamRoster__header TeamRoster__header--center'>
                  {t('player.stats.points')}
                </th>
              )}
              <th className='TeamRoster__header TeamRoster__header--center'>
                {t('player.stats.yellowCards')}
              </th>
              <th className='TeamRoster__header TeamRoster__header--center'>
                {t('player.stats.redCards')}
              </th>
              <th className='TeamRoster__header' />
            </tr>
          </thead>
          <tbody>
            {roster?.standard.map(player => (
              <TeamRosterPlayer
                key={player.id}
                player={player}
                subtitle={t('team.roster.standard')}
                prefix={prefix}
                disableAssists={disableAssists}
                disablePoints={disablePoints}
                facr={facr}
              />
            ))}
            {roster?.hosting.length > 0 && (
              <>
                <tr>
                  <th colSpan={3} className='TeamRoster__section' />
                </tr>
                {roster.hosting.map(player => (
                  <TeamRosterPlayer
                    key={player.id}
                    player={player}
                    subtitle={t('team.roster.hosting')}
                    prefix={prefix}
                    disableAssists={disableAssists}
                    disablePoints={disablePoints}
                    facr={facr}
                  />
                ))}
              </>
            )}
            {roster?.alternating.length > 0 && (
              <>
                <tr>
                  <th colSpan={3} className='TeamRoster__section' />
                </tr>
                {roster.alternating.map(player => (
                  <TeamRosterPlayer
                    key={player.id}
                    player={player}
                    subtitle={t('team.roster.alternating')}
                    prefix={prefix}
                    disableAssists={disableAssists}
                    disablePoints={disablePoints}
                    facr={facr}
                  />
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TeamRoster;
